import React, { useLayoutEffect, useState } from 'react';

import isEqual from 'lodash/isEqual';

import type { HelpCenterSiteTreeNode, SiteNavigationSplit } from './types';
import { splitSiteNavigation } from './split-site-navigation';

const getLeftoverWidth = (row: HTMLElement, slot: HTMLElement): number => {
	const rowWidth = row.getBoundingClientRect().width;
	const gapWidth =
		Number.parseFloat(window.getComputedStyle(row).columnGap) ||
		Number.parseFloat(window.getComputedStyle(row).gap) ||
		0;
	const children = Array.from(row.children) as HTMLElement[];
	const siblingWidth = children
		.filter((child) => child !== slot)
		.reduce((total, child) => total + child.getBoundingClientRect().width, 0);
	const gapCount = Math.max(children.length - 1, 0);

	return rowWidth - siblingWidth - gapCount * gapWidth;
};

export const useSiteNavigationOverflow = ({
	items,
	measurementRowRef,
	slotRef,
}: {
	items: readonly HelpCenterSiteTreeNode[];
	measurementRowRef: React.RefObject<HTMLDivElement>;
	slotRef: React.RefObject<HTMLDivElement>;
}): SiteNavigationSplit => {
	const [split, setSplit] = useState<SiteNavigationSplit>(() => ({
		promotedFolders: [],
		overflowItems: [...items],
	}));

	useLayoutEffect(() => {
		const slot = slotRef.current;
		const measurementRow = measurementRowRef.current;
		const row = slot?.parentElement;
		if (!slot || !measurementRow || !row) {
			return;
		}

		let animationFrame: number | null = null;
		let hasUsableMeasurement = false;
		const measure = () => {
			const folderWidths = Object.fromEntries(
				Array.from(
					measurementRow.querySelectorAll<HTMLElement>('[data-site-navigation-folder-id]'),
					(element) => [
						element.getAttribute('data-site-navigation-folder-id') ?? '',
						element.getBoundingClientRect().width,
					],
				),
			);
			const moreItem = measurementRow.querySelector<HTMLElement>(
				'[data-site-navigation-more-item]',
			);
			const gapWidth = Number.parseFloat(window.getComputedStyle(measurementRow).columnGap) || 0;
			// Centering hugs the slot, so leftover width is the row minus Home /
			// Portals / Requests (and their gaps), not the slot's own box.
			const availableWidth = getLeftoverWidth(row, slot);

			// The navbar row is `display: none` until its responsive breakpoint
			// resolves, so wait until leftover width is usable.
			if (availableWidth <= 0) {
				return;
			}
			hasUsableMeasurement = true;

			const next = splitSiteNavigation(items, {
				availableWidth,
				folderWidths,
				gapWidth,
				moreWidth: moreItem?.getBoundingClientRect().width ?? 0,
			});

			// Deep-compare so renames (same ids, new names) still update the nav.
			setSplit((previous) => (isEqual(previous, next) ? previous : next));
		};
		// Coalesce resize callbacks after the first usable synchronous measure.
		const scheduleMeasurement = () => {
			if (!hasUsableMeasurement) {
				measure();
				return;
			}
			if (animationFrame === null) {
				animationFrame = requestAnimationFrame(() => {
					animationFrame = null;
					measure();
				});
			}
		};

		// Measure before the browser paints so the first frame already shows the
		// correct split, rather than briefly rendering everything under More.
		measure();

		const observer = new ResizeObserver(scheduleMeasurement);
		observer.observe(row);
		observer.observe(measurementRow);

		return () => {
			if (animationFrame !== null) {
				cancelAnimationFrame(animationFrame);
			}
			observer.disconnect();
		};
	}, [items, measurementRowRef, slotRef]);

	return split;
};
